import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Edit, Trash2, Info } from "lucide-react";
import { toast } from "sonner";
import {
  getComponentTypes,
  createSalaryComponent,
  getSalaryComponents,
  updateSalaryComponent,
  deleteSalaryComponent,
  getSettings,
  addReimbursementType,
  updateReimbursementType,
  deleteReimbursementType,
  updateReimbursementSettings,
} from "@/services/adminService";
import { useOnboarding } from "@/contexts/OnboardingContext";
import ConfirmDialog from "@/components/ConfirmDialog";

import {
  TooltipProvider,
  TooltipTrigger,
  Tooltip,
  TooltipContent,
} from "@/components/ui/tooltip";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";

const SalaryComponents = () => {
  const [activeTab, setActiveTab] = useState("earnings");
  const [components, setComponents] = useState([]);
  // Reimbursement claim types (configured here, used in the employee claim form)
  const [reimbursementTypes, setReimbursementTypes] = useState([]);
  const [reimbursementTypeForm, setReimbursementTypeForm] = useState({
    reimbursement_type: "",
    description: "",
    editingId: null,
  });
  const [reimbursementTypeDialogOpen, setReimbursementTypeDialogOpen] =
    useState(false);
  const [savingReimbursementType, setSavingReimbursementType] = useState(false);
  const [claimWindow, setClaimWindow] = useState(90);
  const savedClaimWindowRef = useRef(90); // last-persisted value, for the no-change check
  const [savingClaimWindow, setSavingClaimWindow] = useState(false);
  const [reimbursementTypeToDelete, setReimbursementTypeToDelete] =
    useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [componentToDelete, setComponentToDelete] = useState(null);
  const [componentTypes, setComponentTypes] = useState([]);
  const [benefitAssociations, setBenefitAssociations] = useState([]);
  const { reloadOnboardingStatus } = useOnboarding();

  const [formData, setFormData] = useState({
    component_type: "",
    component_name: "",
    name_in_payslip: "",
    is_variable: false,
    calculation_type: "flat_amount",
    amount_value: 0,
    is_active: true,
    part_of_salary_structure: true,
    is_taxable: true,
    calculate_on_pro_rata: false,
    is_scheduled_earning: false,
    tax_deduction_preference: "subsequent_payrolls",
    include_in_fbp: false,
    consider_for_epf: false,
    epf_contribution_rule: "always",
    pf_wage_threshold: 15000,
    consider_for_esi: false,
    show_in_payslip: true,
    // Deduction specific
    deduction_frequency: "one_time",

    selected_type: "",
    custom_type: "",
  });

  const { can } = usePermission();
  const { canCreate, canEdit, canDelete } = createCanHelpers(can);
  const canCreateSalaryComponent = canCreate("salary_components_configuration");
  const canEditSalaryComponent = canEdit("salary_components_configuration");
  const canDeleteSalaryComponent = canDelete("salary_components_configuration");

  useEffect(() => {
    fetchComponents(activeTab);
    fetchComponentTypes(activeTab);
  }, [activeTab]);

  const fetchComponents = async (category) => {
    setLoading(true);
    try {
      const response = await getSalaryComponents(category);

      setComponents(response.components || []);
    } catch (error) {
      console.error("Error fetching components:", error);
      toast.error("Failed to load salary components");
    } finally {
      setLoading(false);
    }
  };

  const fetchComponentTypes = async (category) => {
    try {
      const response = await getComponentTypes(category);
      const normalized = normalizeTypes(category, response.types);

      setComponentTypes(normalized);
    } catch (error) {
      console.error("Error fetching component types:", error);
    }
  };

  // ----- Reimbursement claim types -----
  const fetchReimbursementTypes = async () => {
    try {
      const res = await getSettings();
      // /admin/settings returns the full settings doc as `data`; reimbursement
      // config (types + claim window) lives under `reimbursement_config`.
      const settings = res?.data ?? res;
      setReimbursementTypes(settings?.reimbursement_config?.types || []);
      const windowDays = settings?.reimbursement_config?.claim_window_days ?? 90;
      setClaimWindow(windowDays);
      savedClaimWindowRef.current = windowDays;
    } catch (error) {
      console.error("Error fetching reimbursement types:", error);
    }
  };

  useEffect(() => {
    fetchReimbursementTypes();
  }, []);

  const handleSaveClaimWindow = async () => {
    const days = Number(claimWindow);
    if (!Number.isInteger(days) || days < 1 || days > 365) {
      toast.error("Claim window must be a whole number between 1 and 365 days");
      return;
    }
    if (days === savedClaimWindowRef.current) {
      toast.info("No change in claim window value");
      return;
    }
    try {
      setSavingClaimWindow(true);
      const res = await updateReimbursementSettings({ claim_window_days: days });
      const saved = res?.data?.claim_window_days ?? days;
      setClaimWindow(saved);
      savedClaimWindowRef.current = saved;
      toast.success("Claim window updated");
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to update claim window"
      );
    } finally {
      setSavingClaimWindow(false);
    }
  };

  const handleSaveReimbursementType = async () => {
    const name = reimbursementTypeForm.reimbursement_type.trim();
    if (!name) return;
    const isEditing = !!reimbursementTypeForm.editingId;
    try {
      setSavingReimbursementType(true);
      const payload = {
        reimbursement_type: name,
        description: reimbursementTypeForm.description.trim(),
      };
      const res = isEditing
        ? await updateReimbursementType(reimbursementTypeForm.editingId, payload)
        : await addReimbursementType(payload);
      setReimbursementTypes(res?.data || []);
      setReimbursementTypeForm({
        reimbursement_type: "",
        description: "",
        editingId: null,
      });
      setReimbursementTypeDialogOpen(false);
      toast.success(
        isEditing ? "Reimbursement type updated" : "Reimbursement type added"
      );
    } catch (error) {
      toast.error("Failed to save reimbursement type");
    } finally {
      setSavingReimbursementType(false);
    }
  };

  const openEditReimbursementType = (t) => {
    setReimbursementTypeForm({
      reimbursement_type: t.reimbursement_type || "",
      description: t.description || "",
      editingId: t._id,
    });
    setReimbursementTypeDialogOpen(true);
  };

  const handleDeleteReimbursementType = async (typeId) => {
    try {
      const res = await deleteReimbursementType(typeId);
      setReimbursementTypes(res?.data || []);
      toast.success("Reimbursement type deleted");
    } catch (error) {
      toast.error("Failed to delete reimbursement type");
    }
  };

  const handleOpenDialog = (component = null) => {
    if (component) {
      const isBenefits = activeTab === "benefits";

      let isCustom = false;

      if (isBenefits) {
        // Flatten all predefined benefit names
        const allBenefitTypes = Object.values(componentTypes)
          .flat()
          .map((x) => x.trim());

        isCustom = !allBenefitTypes.includes(component.component_type);
      } else {
        // earnings / deductions
        isCustom = !componentTypes.includes(component.component_type);
      }

      setEditingComponent(component);

      setFormData({
        ...component,

        // For non-benefits:
        selected_type: isCustom ? "__custom__" : component.component_type,
        custom_type: isCustom ? component.component_type : "",
        component_type: component.component_type,

        // For benefits only:
        ...(isBenefits
          ? {
              benefit_plan: component.benefit_plan,
              benefit_association: component.benefit_association,
            }
          : {}),
      });
    } else {
      resetForm();
      setEditingComponent(null);
    }

    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      component_type: "",
      component_name: "",
      name_in_payslip: "",
      is_variable: false,
      calculation_type: "flat_amount",
      amount_value: 0,
      is_active: true,
      part_of_salary_structure: true,
      is_taxable: true,
      calculate_on_pro_rata: false,
      is_scheduled_earning: false,
      tax_deduction_preference: "subsequent_payrolls",
      include_in_fbp: false,
      consider_for_epf: false,
      epf_contribution_rule: "always",
      pf_wage_threshold: 15000,
      consider_for_esi: false,
      show_in_payslip: true,
      deduction_frequency: "one_time",
      benefit_plan: "",
      benefit_association: "",
      include_employer_contribution: false,
      is_superannuation_fund: false,
      unclaimed_handling: "carry_forward",
      amount_per_month: 0,
    });
    setBenefitAssociations([]);
  };

  const handleSave = async () => {
    // Basic validation
    if (!formData.name_in_payslip) {
      toast.error("Please fill in Name in Payslip");
      return;
    }

    // Category-specific validation
    if (activeTab === "earnings" ) {
      if (!formData.selected_type) {
        toast.error("Please select component type");
        return;
      }

      // If custom selected, ensure it's typed
      if (formData.selected_type === "__custom__") {
        if (!formData.custom_type.trim()) {
          toast.error("Please enter a custom type");
          return;
        }
      }
    }

    try {
      const finalComponentType =
        formData.selected_type === "__custom__"
          ? formData.custom_type.trim()
          : formData.selected_type;

      const payload = {
        ...formData,
        category: activeTab,
        component_type: finalComponentType,
      };
      // Strip deprecated amount fields from payload
      delete payload.amount_value;
      delete payload.amount_per_month;

      if (editingComponent) {
        await updateSalaryComponent(payload, editingComponent._id);
        toast.success("Component updated successfully");
      } else {
        const response = await createSalaryComponent(payload);
        toast.success("Component created successfully");
      }
      reloadOnboardingStatus();
      setIsDialogOpen(false);
      fetchComponents(activeTab);
      fetchComponentTypes(activeTab);
    } catch (error) {
      console.error("Error saving component:", error);
      toast.error(error.response?.data?.detail || "Failed to save component");
    }
  };

  const handleDelete = async () => {
    if (!componentToDelete) return;

    try {
      const response = await deleteSalaryComponent(componentToDelete._id);

      toast.success("Component deleted successfully");
      setIsDeleteDialogOpen(false);
      setComponentToDelete(null);
      fetchComponents(activeTab);
    } catch (error) {
      console.error("Error deleting component:", error);
      toast.error(error.response?.data?.detail || "Failed to delete component");
    }
  };

  const filteredComponents = components.filter(
    (c) =>
      c.component_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.component_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.name_in_payslip?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredReimbursementTypes = reimbursementTypes.filter(
    (t) =>
      t.reimbursement_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryLabel = (category) => {
    const labels = {
      earnings: "Earnings",
      deductions: "Deductions",
    };
    return labels[category] || category;
  };

  // Render form based on active tab
  const renderFormFields = () => {
    switch (activeTab) {
      case "earnings":
        return renderEarningsForm();
      case "deductions":
        return renderDeductionsForm();
      default:
        return null;
    }
  };

  const renderEarningsForm = () => (
    <div className="grid grid-cols-2 gap-6 py-4">
      {/* Left Column */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="component_type">Component Type *</Label>
          <Select
            value={formData.component_type}
            onValueChange={(value) =>
              setFormData({
                ...formData,
                selected_type: value,
                component_type: value === "__custom__" ? "" : value,
                custom_type: value === "__custom__" ? "" : formData.custom_type,
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {Array.isArray(componentTypes) &&
                componentTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}

              <SelectItem value="__custom__">+ Add Custom Type</SelectItem>
            </SelectContent>
          </Select>
          {formData.selected_type === "__custom__" && (
            <Input
              placeholder="Enter custom type"
              className="mt-2"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  custom_type: e.target.value,
                })
              }
            />
          )}
        </div>

        <div>
          <Label htmlFor="component_name">Component Name *</Label>
          <Input
            id="component_name"
            value={formData.component_name}
            onChange={(e) =>
              setFormData({ ...formData, component_name: e.target.value })
            }
            placeholder="e.g., Monthly HRA"
          />
        </div>

        <div>
          <Label htmlFor="name_in_payslip">Name in Payslip *</Label>
          <Input
            id="name_in_payslip"
            value={formData.name_in_payslip}
            onChange={(e) =>
              setFormData({ ...formData, name_in_payslip: e.target.value })
            }
            placeholder="e.g., House Rent Allowance"
          />
        </div>

        {/* <div className="flex items-center space-x-2">
          <Checkbox
            id="is_variable"
            checked={formData.is_variable}
            onCheckedChange={(checked) =>
              setFormData({ ...formData, is_variable: checked })
            }
          />
          <Label htmlFor="is_variable" className="font-normal">
            This is a variable earning (e.g., Bonus, Commission)
          </Label>
        </div> */}

        <div className="flex items-center space-x-2">
          <Checkbox
            id="is_active"
            checked={formData.is_active}
            onCheckedChange={(checked) =>
              setFormData({ ...formData, is_active: checked })
            }
          />
          <Label htmlFor="is_active" className="font-normal">
            Mark this as Active
          </Label>
        </div>
      </div>

      {/* Right Column - Other Configurations */}
      <div className="space-y-4">
        <h3 className="font-semibold text-lg">Other Configurations</h3>

        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="part_of_salary_structure"
              checked={formData.part_of_salary_structure}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, part_of_salary_structure: checked })
              }
              disabled={true}
            />
            <Label htmlFor="part_of_salary_structure" className="font-normal">
              Make this a part of the employee's salary structure
            </Label>
          </div>

          <div className="flex items-start space-x-2">
            <Checkbox
              id="is_taxable"
              checked={formData.is_taxable}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, is_taxable: checked })
              }
              disabled={true}
            />
            <span>
              <Label htmlFor="is_taxable" className="font-normal">
                This is a taxable earning
              </Label>
              <p className="text-xs text-gray-500 mt-1">
                The income tax amount will be divided equally and deducted every
                month across the financial year.
              </p>
            </span>
          </div>

          {formData.is_taxable && formData.is_variable && (
            <div className="ml-6 space-y-2">
              <Label>Tax Deduction Preference</Label>
              <RadioGroup
                value={formData.tax_deduction_preference}
                onValueChange={(value) =>
                  setFormData({ ...formData, tax_deduction_preference: value })
                }
              >
                <div className="flex items-start space-x-2">
                  <RadioGroupItem
                    value="subsequent_payrolls"
                    id="subsequent_payrolls"
                  />
                  <div>
                    <Label
                      htmlFor="subsequent_payrolls"
                      className="font-normal"
                    >
                      Deduct tax in subsequent payrolls of the financial year
                    </Label>
                    <p className="text-xs text-gray-500 mt-1">
                      The income tax amount will be divided equally and deducted
                      every month.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <RadioGroupItem value="same_payroll" id="same_payroll" />
                  <div>
                    <Label htmlFor="same_payroll" className="font-normal">
                      Deduct tax in same payroll
                    </Label>
                    <p className="text-xs text-gray-500 mt-1">
                      The entire income tax amount will be deducted when it is
                      paid.
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>
          )}

          <div className="flex items-start space-x-2">
            <Checkbox
              id="calculate_on_pro_rata"
              checked={formData.calculate_on_pro_rata}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, calculate_on_pro_rata: checked })
              }
            />
            <div>
              <Label htmlFor="calculate_on_pro_rata" className="font-normal">
                Calculate on pro-rata basis
              </Label>
              <p className="text-xs text-gray-500 mt-1">
                Pay will be adjusted based on employee working days.
              </p>
            </div>
          </div>

          {formData.is_variable && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_scheduled_earning"
                checked={formData.is_scheduled_earning}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_scheduled_earning: checked })
                }
              />
              <Label htmlFor="is_scheduled_earning" className="font-normal">
                This is a scheduled earning
              </Label>
            </div>
          )}

          {/* <div className="flex items-start space-x-2">
            <Checkbox
              id="include_in_fbp"
              checked={formData.include_in_fbp}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, include_in_fbp: checked })
              }
            />
            <div>
              <Label htmlFor="include_in_fbp" className="font-normal">
                Include this as a Flexible Benefit Plan component
              </Label>
              <p className="text-xs text-gray-500 mt-1">
                FBP allows employees to personalize their salary structure.
              </p>
            </div>
          </div> */}

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="consider_for_epf"
                checked={formData.consider_for_epf}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, consider_for_epf: checked })
                }
              />
              <Label htmlFor="consider_for_epf" className="font-normal">
                Consider for EPF Contribution
              </Label>
            </div>

            {formData.consider_for_epf && (
              <RadioGroup
                value={formData.epf_contribution_rule}
                onValueChange={(value) =>
                  setFormData({ ...formData, epf_contribution_rule: value })
                }
                className="ml-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="always" id="epf_always" />
                  <Label htmlFor="epf_always" className="font-normal">
                    Always
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="conditional" id="epf_conditional" />
                  <Label htmlFor="epf_conditional" className="font-normal">
                    Only when PF Wage is less than ₹15,000
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="inline w-4 h-4 ml-1 text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent>
                          If the sum of PF Wage exceeds ₹ 15,000 when this
                          component is included, the EPF will be calculated only
                          for a maximum amount of ₹ 15,000.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                </div>
              </RadioGroup>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="consider_for_esi"
              checked={formData.consider_for_esi}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, consider_for_esi: checked })
              }
            />
            <Label htmlFor="consider_for_esi" className="font-normal">
              Consider for ESI Contribution
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="show_in_payslip"
              checked={formData.show_in_payslip}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, show_in_payslip: checked })
              }
              disabled={true}
            />
            <Label htmlFor="show_in_payslip" className="font-normal">
              Show this component in payslip
            </Label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDeductionsForm = () => (
    <div className="space-y-4 py-4">
      <div>
        <Label htmlFor="name_in_payslip">Name in Payslip *</Label>
        <Input
          id="name_in_payslip"
          value={formData.name_in_payslip}
          onChange={(e) =>
            setFormData({ ...formData, name_in_payslip: e.target.value })
          }
          placeholder="e.g., Provident Fund Deduction"
        />
      </div>

      <div>
        <Label>Select the deduction frequency *</Label>
        <RadioGroup
          value={formData.deduction_frequency}
          onValueChange={(value) =>
            setFormData({ ...formData, deduction_frequency: value })
          }
          className="mt-2"
        >
          <div className="flex items-start space-x-2">
            <RadioGroupItem value="one_time" id="one_time" />
            <div>
              <Label htmlFor="one_time" className="font-normal">
                One-time deduction
              </Label>
              <p className="text-xs text-gray-500 mt-1">
                The deduction will only be applied once.
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <RadioGroupItem value="recurring" id="recurring" />
            <div>
              <Label htmlFor="recurring" className="font-normal">
                Recurring deduction for subsequent Payrolls
              </Label>
              <p className="text-xs text-gray-500 mt-1">
                The deduction will be applied in every payroll cycle going
                forward.
              </p>
            </div>
          </div>
        </RadioGroup>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="is_active"
          checked={formData.is_active}
          onCheckedChange={(checked) =>
            setFormData({ ...formData, is_active: checked })
          }
        />
        <Label htmlFor="is_active" className="font-normal">
          Mark this as Active
        </Label>
      </div>
    </div>
  );

  const renderBenefitsForm = () => (
    <div className="space-y-4 py-4">
      <div>
        <Label htmlFor="benefit_plan">Benefit Plan *</Label>
        <Select
          value={formData.benefit_plan}
          onValueChange={(value) => {
            setFormData({
              ...formData,
              benefit_plan: value,
              benefit_association: "",
            });
            setBenefitAssociations(componentTypes[value] || []);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select benefit plan" />
          </SelectTrigger>
          <SelectContent>
            {Object.keys(componentTypes).map((plan) => (
              <SelectItem key={plan} value={plan}>
                {plan}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {formData.benefit_plan && (
        <div>
          <Label htmlFor="benefit_association">
            Associate this benefit with *
          </Label>
          <Select
            value={formData.benefit_association}
            onValueChange={(value) =>
              setFormData({ ...formData, benefit_association: value })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select association" />
            </SelectTrigger>
            <SelectContent>
              {benefitAssociations.map((assoc) => (
                <SelectItem key={assoc} value={assoc}>
                  {assoc}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label htmlFor="name_in_payslip">Name in Payslip *</Label>
        <Input
          id="name_in_payslip"
          value={formData.name_in_payslip}
          onChange={(e) =>
            setFormData({ ...formData, name_in_payslip: e.target.value })
          }
          placeholder="e.g., LIC Premium"
        />
      </div>

      <div className="flex items-start space-x-2">
        <Checkbox
          id="include_employer_contribution"
          checked={formData.include_employer_contribution}
          onCheckedChange={(checked) =>
            setFormData({ ...formData, include_employer_contribution: checked })
          }
        />
        <div>
          <Label
            htmlFor="include_employer_contribution"
            className="font-normal"
          >
            Include employer's contribution in employee's salary structure
          </Label>
          <p className="text-xs text-gray-500 mt-1">
            Enable this if employer contributions should be part of CTC.
          </p>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="is_superannuation_fund"
          checked={formData.is_superannuation_fund}
          onCheckedChange={(checked) =>
            setFormData({ ...formData, is_superannuation_fund: checked })
          }
        />
        <Label htmlFor="is_superannuation_fund" className="font-normal">
          Consider this a superannuation fund
        </Label>
      </div>

      <div className="flex items-start space-x-2">
        <Checkbox
          id="calculate_on_pro_rata"
          checked={formData.calculate_on_pro_rata}
          onCheckedChange={(checked) =>
            setFormData({ ...formData, calculate_on_pro_rata: checked })
          }
        />
        <div>
          <Label htmlFor="calculate_on_pro_rata" className="font-normal">
            Calculate on pro-rata basis
          </Label>
          <p className="text-xs text-gray-500 mt-1">
            Benefit will be adjusted based on employee working days.
          </p>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="is_active"
          checked={formData.is_active}
          onCheckedChange={(checked) =>
            setFormData({ ...formData, is_active: checked })
          }
        />
        <Label htmlFor="is_active" className="font-normal">
          Mark this as Active
        </Label>
      </div>
    </div>
  );

  const normalizeTypes = (category, data) => {
    if (Array.isArray(data)) return data;
    return []; // prevent crashes
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Salary Components
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Configure earnings and deductions
          </p>
        </div>
        {activeTab === "reimbursement_types" ? (
          <Button
            onClick={() => {
              setReimbursementTypeForm({
                reimbursement_type: "",
                description: "",
                editingId: null,
              });
              setReimbursementTypeDialogOpen(true);
            }}
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Reimbursement Type
          </Button>
        ) : (
          <Button
            disabled={!canCreateSalaryComponent}
            onClick={() => {
              if (!canCreateSalaryComponent) return;
              handleOpenDialog();
            }}
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Component
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex justify-start space-x-8 overflow-x-auto bg-transparent h-auto p-0 no-scrollbar">
            <TabsTrigger value="earnings">Earnings</TabsTrigger>
            <TabsTrigger value="deductions">Deductions</TabsTrigger>
            <TabsTrigger value="reimbursement_types">
              Reimbursement Types
            </TabsTrigger>
          </TabsList>

        {["earnings", "deductions"].map((category) => (
          <TabsContent key={category} value={category}>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{getCategoryLabel(category)}</CardTitle>
                    <CardDescription>
                      Manage {category} components for your payroll system
                    </CardDescription>
                  </div>
                  <div className="w-72">
                    <Input
                      placeholder="Search components..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3 py-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Skeleton key={i} className="h-14 w-full" />
                    ))}
                  </div>
                ) : filteredComponents.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No {category} components found. Add your first component to
                    get started.
                  </div>
                ) : (
                  <div className="rounded-md border border-gray-200 dark:border-gray-700 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name in Payslip</TableHead>
                          {category === "earnings" && (
                            <TableHead>Type</TableHead>
                          )}
                          {category === "earnings" && (
                            <TableHead>Calculation</TableHead>
                          )}
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredComponents.map((component) => (
                          <TableRow key={component.component_id}>
                            <TableCell className="font-medium">
                              {component.name_in_payslip}
                            </TableCell>
                            {category === "earnings" && (
                              <TableCell>{component.component_type}</TableCell>
                            )}
                            {category === "earnings" && (
                              <TableCell>
                                {component.is_variable ? (
                                  <Badge variant="outline">Variable</Badge>
                                ) : (
                                  <>
                                    {component.calculation_type ===
                                      "flat_amount" && `Flat Amount`}
                                    {component.calculation_type ===
                                      "percentage_of_ctc" &&
                                      `Percentage of CTC`}
                                    {component.calculation_type ===
                                      "percentage_of_basic" &&
                                      `Percentage of Basic`}
                                  </>
                                )}
                              </TableCell>
                            )}
                            <TableCell>
                              {component.is_active ? (
                                <Badge
                                  variant="success"
                                  className="bg-green-100 text-green-800"
                                >
                                  Active
                                </Badge>
                              ) : (
                                <Badge variant="secondary">Inactive</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={!canEditSalaryComponent}
                                onClick={() => {
                                  if (!canEditSalaryComponent) return;
                                  handleOpenDialog(component);
                                }}
                                className="mr-2"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={!canDeleteSalaryComponent}
                                onClick={() => {
                                  setComponentToDelete(component);
                                  setIsDeleteDialogOpen(true);
                                }}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}

        <TabsContent value="reimbursement_types">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Reimbursement Types</CardTitle>
                  <CardDescription>
                    Manage the reimbursement claim types employees can choose
                    from when filing a reimbursement.
                  </CardDescription>
                </div>
                <div className="w-72">
                  <Input
                    placeholder="Search reimbursement types..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredReimbursementTypes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No reimbursement types yet. Add one so employees can file
                  claims.
                </div>
              ) : (
                <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredReimbursementTypes.map((t) => (
                        <TableRow key={t._id}>
                          <TableCell className="font-medium">
                            {t.reimbursement_type}
                          </TableCell>
                          <TableCell className="text-muted-foreground max-w-md">
                            {t.description || "—"}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditReimbursementType(t)}
                            >
                              <Edit className="w-4 h-4 text-gray-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setReimbursementTypeToDelete(t)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Company-wide claim date window — its own card, below the table */}
          <Card className="mt-6">
            <CardHeader>
              <div className="flex flex-wrap justify-between gap-4">
                <div>
                  <CardTitle>Claim Window</CardTitle>
                  <CardDescription>
                    Employees can only claim expenses dated within this many
                    days.
                  </CardDescription>
                </div>
                <div className="flex items-end gap-3">
                  <div className="space-y-1">
                    <Label>Claim window (days)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="365"
                      step="1"
                      value={claimWindow}
                      onKeyDown={(e) => {
                        if (["e", "E", "+", "-", "."].includes(e.key))
                          e.preventDefault();
                      }}
                      onChange={(e) =>
                        setClaimWindow(e.target.value.replace(/\D/g, ""))
                      }
                      className="w-32"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleSaveClaimWindow}
                    disabled={savingClaimWindow}
                  >
                    {savingClaimWindow ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Add Reimbursement Type Dialog */}
          <Dialog
            open={reimbursementTypeDialogOpen}
            onOpenChange={setReimbursementTypeDialogOpen}
          >
            <DialogContent className="max-w-md border dark:border-gray-700 rounded-lg">
              <DialogHeader>
                <DialogTitle>
                  {reimbursementTypeForm.editingId ? "Edit" : "Add"} Reimbursement
                  Type
                </DialogTitle>
                <DialogDescription>
                  The name appears in the employee claim dropdown.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Type Name</Label>
                  <Input
                    value={reimbursementTypeForm.reimbursement_type}
                    onChange={(e) =>
                      setReimbursementTypeForm((p) => ({
                        ...p,
                        reimbursement_type: e.target.value,
                      }))
                    }
                    placeholder="e.g. Travel, Meals, Internet..."
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={reimbursementTypeForm.description}
                    onChange={(e) =>
                      setReimbursementTypeForm((p) => ({
                        ...p,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Internal notes / Type description..."
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setReimbursementTypeDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveReimbursementType}
                  disabled={
                    savingReimbursementType ||
                    !reimbursementTypeForm.reimbursement_type.trim()
                  }
                >
                  {savingReimbursementType ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete confirmation */}
          <ConfirmDialog
            open={!!reimbursementTypeToDelete}
            onOpenChange={(o) => {
              if (!o) setReimbursementTypeToDelete(null);
            }}
            title="Delete reimbursement type?"
            message={
              reimbursementTypeToDelete
                ? `"${reimbursementTypeToDelete.reimbursement_type}" will be removed. Existing claims keep their saved type.`
                : ""
            }
            confirmText="Delete"
            cancelText="Cancel"
            destructive
            onConfirm={() =>
              handleDeleteReimbursementType(reimbursementTypeToDelete?._id)
            }
          />
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingComponent ? "Edit" : "New"} {getCategoryLabel(activeTab)}
            </DialogTitle>
            <DialogDescription>
              Configure the component details and settings
            </DialogDescription>
          </DialogHeader>

          {renderFormFields()}

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
            <div className="flex items-start">
              <Info className="w-5 h-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <strong>Note:</strong> Once you associate this component with an
                employee, you will only be able to edit the Name in Payslip. The
                changes you make will apply only to new employees.
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-primary hover:bg-primary/90"
            >
              {editingComponent ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the component "
              {componentToDelete?.name_in_payslip}". This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SalaryComponents;
